import {
  CSSProperties,
  Fragment,
  ReactNode,
  Ref,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Menu, Transition } from "@headlessui/react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/pro-regular-svg-icons";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import cx from "classnames";

export interface DropDownMenuItem {
  text: string;
  /**
   * English fallback for `text` when it is a translation key. Without one, a key that has
   * not been seeded into the translations table yet renders as the raw key in the menu.
   */
  defaultText?: string;
  /** Set a value if the menu's EventType is "NAVIGATE" */
  href?: string | undefined;
  /** Used for custom click events on items */
  customArg?: any | undefined;
  customIcon?: ReactNode | undefined;
  disabled?: boolean | undefined;
  disabledMsg?: string;
  subText?: string | undefined;
  /**
   * Lets a single item perform an action inside an otherwise "NAVIGATE" menu,
   * so the other items keep their `Link` behaviour (open in new tab, etc.).
   */
  onSelect?(): void;
}

export interface DropDownMenuProps {
  menuButtonClassName?: string;
  outerDivClassName?: string;
  eventType: "NAVIGATE" | "EVENT";
  items: DropDownMenuItem[];
  customButtonContents?: ReactNode | undefined;
  isWithinButtonElement?: boolean;
  onItemClick?(itemIndex: number, customArg: any | undefined): void | undefined;
  showMenuEvenIfOnlyOneItem?: boolean;
  /**
   * Renders the open menu into `document.body` instead of next to the button, so it isn't
   * clipped by an ancestor with `overflow: hidden`/`auto` (e.g. the journey widget).
   */
  renderMenuInPortal?: boolean;
}

/** Keeps a portalled menu aligned to its button. Hooks can't live inside the `Menu` render prop. */
const PortalMenuPositionSync = ({
  open,
  updatePosition,
}: {
  open: boolean;
  updatePosition: () => void;
}) => {
  useLayoutEffect(() => {
    if (!open) return;

    updatePosition();

    window.addEventListener("resize", updatePosition);
    // Capture phase so scrolling of any ancestor container is picked up, not just the window
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open, updatePosition]);

  return null;
};

/** A form field containing a dropdownlist (i.e. a `select` element) */
const DropDownMenu = ({
  items,
  eventType,
  onItemClick,
  customButtonContents = undefined,
  isWithinButtonElement = false,
  menuButtonClassName = "inline-flex justify-center w-full bg-white text-sm font-medium text-gray-700 rounded-full hover:bg-gray-100 focus:outline-none",
  outerDivClassName = "relative inline-block text-left",
  showMenuEvenIfOnlyOneItem = false,
  renderMenuInPortal = false,
}: DropDownMenuProps) => {
  const { t } = useTranslation();

  // Falling back to the key itself preserves the behaviour of items that pass no
  // `defaultText`, several of which pass plain English rather than a key.
  const itemText = (item: DropDownMenuItem) =>
    t(item.text, item.defaultText ?? item.text);

  const buttonRef = useRef<HTMLElement | null>(null);
  const [portalPosition, setPortalPosition] = useState<CSSProperties | null>(
    null
  );

  const updatePortalPosition = useCallback(() => {
    if (!buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    const viewportHeight = document.documentElement.clientHeight;
    const right = document.documentElement.clientWidth - rect.right;

    // Rough height estimate, so we can flip the menu without a measure-then-move flicker.
    // Being a little out only affects which side we open on, never whether the menu renders.
    const estimatedHeight = items.length * 44 + 8;
    const spaceBelow = viewportHeight - rect.bottom;
    const openUpwards =
      spaceBelow < estimatedHeight + 4 && rect.top > spaceBelow;

    setPortalPosition(
      openUpwards
        ? { bottom: viewportHeight - rect.top + 4, right }
        : { top: rect.bottom + 4, right }
    );
  }, [items.length]);

  const handleItemClick = (index: number, clickedItem: DropDownMenuItem) => {
    if (onItemClick) {
      onItemClick(index, clickedItem.customArg);
    }
  };

  const handleOnlyItemClick = () => {
    // If there's only one item, we display a single button instead of a dropdown
    // and that needs a different click handler
    if (onItemClick) {
      onItemClick(0, items[0].customArg);
    }
  };

  const menuButtonContents =
    customButtonContents === undefined ? (
      <FontAwesomeIcon icon={faChevronDown} className="text-gray-500 m-4" />
    ) : (
      customButtonContents
    );

  if (items && items.length === 1 && !showMenuEvenIfOnlyOneItem) {
    const oneItem = items[0];
    const oneItemInner = (
      <>
        {itemText(oneItem)}
        {oneItem.subText && oneItem.subText.length > 0 && (
          <p className="text-xs text-gray-400">{oneItem.subText}</p>
        )}
      </>
    );
    return (
      <div className={outerDivClassName}>
        {eventType === "NAVIGATE" && !oneItem.onSelect && (
          <Link
            to={oneItem.href || ""}
            className={cx("btn-primary !px-4", menuButtonClassName)}
          >
            {oneItemInner}
          </Link>
        )}
        {eventType === "NAVIGATE" && oneItem.onSelect && (
          <button
            type="button"
            onClick={oneItem.onSelect}
            className={cx("btn-primary !px-4", menuButtonClassName)}
          >
            {oneItemInner}
          </button>
        )}
        {eventType === "EVENT" && (
          <button
            onClick={handleOnlyItemClick}
            className={cx("btn-primary !px-4", menuButtonClassName)}
          >
            {oneItemInner}
          </button>
        )}
      </div>
    );
  }

  const renderMenuItems = (
    menuItemsClassName: string,
    menuItemsStyle?: CSSProperties
  ) => (
    <Transition
      as={Fragment}
      enter="transition ease-out duration-100"
      enterFrom="transform opacity-0 scale-95"
      enterTo="transform opacity-100 scale-100"
      leave="transition ease-in duration-75"
      leaveFrom="transform opacity-100 scale-100"
      leaveTo="transform opacity-0 scale-95"
    >
      <Menu.Items className={menuItemsClassName} style={menuItemsStyle}>
        <div className="py-1">
          {items.map((item, itemIndex) => (
            <Menu.Item key={item.text}>
              <div>
                {eventType === "NAVIGATE" && !item.onSelect && (
                  <Link
                    to={item.href || ""}
                    className="text-sm block my-1 text-gray-600 font-medium hover:bg-gray-100 py-2 px-4 cursor-pointer"
                  >
                    {item.customIcon ? (
                      <span className="mr-1">{item.customIcon}</span>
                    ) : null}
                    {itemText(item)}
                  </Link>
                )}
                {eventType === "NAVIGATE" && item.onSelect && (
                  <button
                    type="button"
                    onClick={item.onSelect}
                    className="text-sm block w-full text-left my-1 text-gray-600 font-medium hover:bg-gray-100 py-2 px-4 cursor-pointer"
                  >
                    {item.customIcon ? (
                      <span className="mr-1">{item.customIcon}</span>
                    ) : null}
                    {itemText(item)}
                  </button>
                )}
                {eventType === "EVENT" && (
                  <span
                    className={cx(
                      "text-sm block my-1 font-medium py-2 px-4",
                      !item.disabled
                        ? "text-gray-600 hover:bg-gray-100 cursor-pointer"
                        : "text-gray-400 bg-gray-100 cursor-not-allowed"
                    )}
                    onClick={
                      !item.disabled
                        ? () => handleItemClick(itemIndex, item)
                        : () => {}
                    }
                  >
                    {item.customIcon ? (
                      <span className="mr-2 text-gray-500">
                        {item.customIcon}
                      </span>
                    ) : null}
                    {itemText(item)}
                    {item.subText && item.subText.length > 0 && (
                      <p
                        className={`text-xs text-gray-400 ${
                          item.disabled ? "mb-1" : ""
                        }`}
                      >
                        {item.subText}
                      </p>
                    )}
                    {item.disabled && item.disabledMsg && (
                      <p className="text-xs text-red-500">{item.disabledMsg}</p>
                    )}
                  </span>
                )}
              </div>
            </Menu.Item>
          ))}
        </div>
      </Menu.Items>
    </Transition>
  );

  return (
    <>
      {items.length > 0 && (
        <Menu as="div" className={cx("z-1", outerDivClassName)}>
          {({ open }) => (
            <>
              {renderMenuInPortal && (
                <PortalMenuPositionSync
                  open={open}
                  updatePosition={updatePortalPosition}
                />
              )}
              <Menu.Button
                ref={buttonRef as Ref<HTMLButtonElement>}
                className={menuButtonClassName}
                as={isWithinButtonElement ? "span" : undefined}
              >
                {menuButtonContents}
              </Menu.Button>

              {!renderMenuInPortal &&
                renderMenuItems(
                  "z-50 origin-top-right absolute right-0 mt-1 w-56 rounded-sm shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none cursor-pointer"
                )}

              {renderMenuInPortal &&
                portalPosition &&
                createPortal(
                  renderMenuItems(
                    "z-[9999] origin-top-right fixed w-56 rounded-sm shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none cursor-pointer",
                    portalPosition
                  ),
                  document.body
                )}
            </>
          )}
        </Menu>
      )}
    </>
  );
};

export default DropDownMenu;
